import { useEffect, useMemo } from "react";

import { AgvanceAPI, SkyAPI } from "@ai360/core";

import {
    getLastUsedLocationGuid,
    getTheUserCompanyGuid,
    getTheUserPersonalityId,
    getUser,
} from "~/login/selectors";
import { messages as globalMessages } from "~/i18n-messages";
import { actions as notificationActions, MSGTYPE } from "~/notifications";
import { useAppDispatch, useAppSelector } from "~/store/hooks";

import {
    isLinkedProductOnlyEnabled,
    toAgvanceLocationGuids,
    toAgvanceLocationIds,
    toLinkedProductGuids,
} from "./agvance-linked-products";

const EMPTY_AGVANCE_LOCATIONS = [] as SkyAPI.ILocationResponse[];
const EMPTY_LINKED_PRODUCTS = [] as AgvanceAPI.IProduct[];

export interface ILinkedProductRestrictionOptions {
    customerGuid?: string | null;
    isEnabled?: boolean;
    /** Defaults to getTheUserCompanyGuid. Ignored when customerGuid is supplied. */
    orgLevelGuid?: string | null;
    scopeCompany?: { linkedProductOnly: boolean; personalityId: number } | null;
}

export interface ILinkedProductRestriction {
    isFetching: boolean;
    /** Never mark a product or block a save until this is true. */
    isRestrictionSettled: boolean;
    /** AgBytes product guids linked in Agvance. null means unrestricted. */
    linkedProductGuids: Set<string> | null;
}

export const useLinkedProductRestriction = ({
    customerGuid = null,
    isEnabled = true,
    orgLevelGuid = null,
    scopeCompany = null,
}: ILinkedProductRestrictionOptions = {}): ILinkedProductRestriction => {
    const dispatch = useAppDispatch();
    const user = useAppSelector(getUser);
    const personalityId = useAppSelector(getTheUserPersonalityId);
    const userCompanyGuid = useAppSelector(getTheUserCompanyGuid);
    const lastUsedLocationGuid = useAppSelector(getLastUsedLocationGuid);

    const resolvedOrgLevelGuid = orgLevelGuid ?? (lastUsedLocationGuid || userCompanyGuid);

    const isGateOpen = scopeCompany
        ? isLinkedProductOnlyEnabled(
              { linkedProductOnly: scopeCompany.linkedProductOnly },
              scopeCompany.personalityId
          )
        : isLinkedProductOnlyEnabled(user, personalityId);

    const isLinkedProductOnly =
        isEnabled && isGateOpen && Boolean(customerGuid || resolvedOrgLevelGuid);

    const scopeKey = useMemo(
        () => (customerGuid ? { customerGuid } : { orgLevelGuid: resolvedOrgLevelGuid }),
        [customerGuid, resolvedOrgLevelGuid]
    );

    const {
        data: agvanceLocations = EMPTY_AGVANCE_LOCATIONS,
        isFetching: isAgvanceLocationsLoading,
        isSuccess: isAgvanceLocationsSuccess,
        isError: isAgvanceLocationsError,
        error: agvanceLocationsError,
    } = SkyAPI.useGetLocationsQuery(
        {
            ...scopeKey,
            userLocationRestrictionType: SkyAPI.LocationRestrictionType.Product,
        },
        { skip: !isLinkedProductOnly, refetchOnMountOrArgChange: true }
    );

    const agvanceLocationIds = useMemo(
        () => toAgvanceLocationIds(agvanceLocations),
        [agvanceLocations]
    );

    const agvanceLocationGuids = useMemo(
        () => toAgvanceLocationGuids(agvanceLocations),
        [agvanceLocations]
    );

    const skipLinkedProducts = !isLinkedProductOnly || agvanceLocationIds.length === 0;

    const {
        data: linkedProducts = EMPTY_LINKED_PRODUCTS,
        isFetching: isLinkedProductsLoading,
        isSuccess: isLinkedProductsSuccess,
        isError: isLinkedProductsError,
        error: linkedProductsError,
    } = AgvanceAPI.useGetLinkedProductListQuery(
        { locationIds: agvanceLocationIds, locationGuids: agvanceLocationGuids, ...scopeKey },
        { skip: skipLinkedProducts, refetchOnMountOrArgChange: true }
    );

    const hasLinkedProductFetchError = isAgvanceLocationsError || isLinkedProductsError;

    const linkedProductGuids = useMemo(
        () =>
            !isLinkedProductOnly || hasLinkedProductFetchError
                ? null
                : new Set(toLinkedProductGuids(linkedProducts)),
        [isLinkedProductOnly, hasLinkedProductFetchError, linkedProducts]
    );

    useEffect(() => {
        if (isAgvanceLocationsError) {
            dispatch(notificationActions.apiCallError(agvanceLocationsError));
        } else if (isLinkedProductsError) {
            dispatch(notificationActions.apiCallError(linkedProductsError));
        }
    }, [
        isAgvanceLocationsError,
        isLinkedProductsError,
        agvanceLocationsError,
        linkedProductsError,
        dispatch,
    ]);

    const hasEmptyLinkedLocations =
        isLinkedProductOnly &&
        !isAgvanceLocationsError &&
        isAgvanceLocationsSuccess &&
        !isAgvanceLocationsLoading &&
        agvanceLocationIds.length === 0;

    const hasEmptyLinkedProducts =
        isLinkedProductOnly &&
        !skipLinkedProducts &&
        !isLinkedProductsError &&
        isLinkedProductsSuccess &&
        !isLinkedProductsLoading &&
        linkedProducts.length === 0;

    useEffect(() => {
        if (hasEmptyLinkedLocations || hasEmptyLinkedProducts) {
            dispatch(
                notificationActions.pushToasterMessage(
                    globalMessages.noLinkedAgvanceProducts,
                    MSGTYPE.INFO
                )
            );
        }
    }, [hasEmptyLinkedLocations, hasEmptyLinkedProducts, dispatch]);

    const isAgvanceLocationsSettled =
        !isAgvanceLocationsLoading && (isAgvanceLocationsSuccess || isAgvanceLocationsError);
    const isLinkedProductsSettled =
        skipLinkedProducts ||
        (!isLinkedProductsLoading && (isLinkedProductsSuccess || isLinkedProductsError));

    return {
        isFetching: isAgvanceLocationsLoading || isLinkedProductsLoading,
        isRestrictionSettled:
            !isLinkedProductOnly || (isAgvanceLocationsSettled && isLinkedProductsSettled),
        linkedProductGuids,
    };
};
